/**
 * file-category — single source of truth for "what kind of preview does
 * this file get?". Used by <FilePreview> to dispatch into image / video /
 * audio / html / pdf / text / binary branches.
 *
 * Order matters for overlap: HTML_EXTS is checked BEFORE TEXT_EXTS so
 * .html / .htm route to the iframe renderer (the html kind) instead of
 * the generic text-snippet renderer — even though 'html' also appears
 * in TEXT_EXTS for code-style snippet rendering elsewhere.
 */

import { isMarkdownFile } from "@/lib/markdown";

export type PreviewKind =
  | "image"
  | "video"
  | "audio"
  | "html"
  | "pdf"
  | "text"
  | "binary";

const IMAGE_EXTS = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "avif",
]);
const VIDEO_EXTS = new Set(["mp4", "webm", "mov", "m4v"]);
const AUDIO_EXTS = new Set(["mp3", "wav", "ogg", "m4a", "flac"]);
const HTML_EXTS = new Set(["html", "htm"]);
const PDF_EXTS = new Set(["pdf"]);
const TEXT_EXTS = new Set([
  "md",
  "mdx",
  "markdown",
  "txt",
  "log",
  "rtf",
  "json",
  "yaml",
  "yml",
  "xml",
  "toml",
  "csv",
  "tsv",
  "js",
  "ts",
  "jsx",
  "tsx",
  "py",
  "rb",
  "go",
  "rs",
  "java",
  "c",
  "cpp",
  "h",
  "css",
  "scss",
  "html",
  "sh",
  "bash",
  "zsh",
  "sql",
  "swift",
  "kt",
  "php",
  "lua",
  "r",
]);

/** Text-ish mimes that don't live under `text/*`. Structured suffixes
 *  (`+json`, `+xml`, `+yaml`) are matched separately, so `application/
 *  atom+xml` and friends don't each need a line here. */
const TEXT_MIMES = new Set([
  "application/json",
  "application/ld+json",
  "application/x-ndjson",
  "application/xml",
  "application/yaml",
  "application/x-yaml",
  "application/toml",
  "application/javascript",
  "application/x-javascript",
  "application/ecmascript",
  "application/x-sh",
  "application/x-shellscript",
  "application/sql",
  "application/csv",
]);

function isTextMime(mimeType?: string | null): boolean {
  const mime = (mimeType ?? "").toLowerCase().split(";")[0].trim();
  if (!mime) return false;
  // html and markdown are text on the wire but have richer renderers of
  // their own; claiming them here would demote them to a raw <pre>.
  if (mime === "text/html" || mime === "text/markdown") return false;
  if (mime.startsWith("text/")) return true;
  if (TEXT_MIMES.has(mime)) return true;
  return /\+(json|xml|yaml)$/.test(mime);
}

export function extOf(filename: string): string {
  const i = filename.lastIndexOf(".");
  return i > 0 ? filename.slice(i + 1).toLowerCase() : "";
}

export function categorise(filename: string): PreviewKind {
  const ext = extOf(filename);
  if (IMAGE_EXTS.has(ext)) return "image";
  if (VIDEO_EXTS.has(ext)) return "video";
  if (AUDIO_EXTS.has(ext)) return "audio";
  if (HTML_EXTS.has(ext)) return "html";
  if (PDF_EXTS.has(ext)) return "pdf";
  if (TEXT_EXTS.has(ext)) return "text";
  return "binary";
}

/**
 * "Does this render as plain text?" — the one predicate every preview surface
 * asks (founder, 08-10: "no preview for text files"). It reads the CLASS off
 * the same extension table `categorise` uses, so a .txt, a .log, a .csv and a
 * .py can never drift apart the way markdown once drifted from plain text.
 *
 * Markdown and html are text on the wire but answer FALSE here: each has a
 * richer renderer, and claiming them would demote a typeset page to a <pre>.
 *
 * Precedence — a KNOWN extension always outranks the mime, because storage
 * mislabels bytes far more often than a user misnames a file: a `deck.pdf`
 * served as `text/plain` must never become a page of binary. Only when the
 * filename teaches us nothing (`blob.bin`, no extension at all) does the mime
 * decide.
 */
export function isPlainTextFile(
  filename?: string | null,
  mimeType?: string | null
): boolean {
  if (isMarkdownFile(filename, mimeType)) return false;
  const name = filename ?? "";
  const kind = name ? categorise(name) : "binary";
  if (kind === "text") return true;
  if (kind === "binary") return isTextMime(mimeType);
  return false;
}
